import CircularProgress from "@mui/material/CircularProgress";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useSendToKitchen from "../../apis/useSendToKitchen";
import Condition from "../../components/Conditional/Condition";
import PopupDialog from "../../components/elements/PopupDialog/PopupDialog";
import { useCartActions } from "../../contexts/CartServiceContext/CartServiceContext";
import { usePaymentActions, usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";
import PaymentDetail from "../PaymentPage/PaymentDetail/PaymentDetail";
import "./SendToKitchenConfirmPage.scss";

const SendToKitchenConfirmPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const cartActions = useCartActions();
	const { state: paymentData } = usePaymentContext((state) => state.paymentData);
	const paymentActions = usePaymentActions();
	const { sendToKitchen, isLoading } = useSendToKitchen();
	const [showErrorPopup, setShowErrorPopup] = useState(false);

	useEffect(() => {
		if (!paymentData) {
			navigate(PATH.CART, { replace: true });
		}
	}, [paymentData, navigate]);

	useEffect(() => {
		return () => {
			paymentActions.CLEAR_PAYMENT_DATA();
		};
	}, [paymentActions]);

	const handleConfirm = useCallback(async () => {
		const err = await sendToKitchen();

		if (err) {
			setShowErrorPopup(true);

			return;
		}
		cartActions.CLEAN_CART();
		navigate(PATH.BILL, { replace: true });
	}, [sendToKitchen, cartActions, navigate]);

	const errorButtons = [
		{
			text: t("sendToKitchenConfirm.errorClose"),
			onClick: () => setShowErrorPopup(false),
		},
	];

	return (
		<div className="SendToKitchenConfirmPage">
			<h2 className="SendToKitchenConfirmTitle">{t("sendToKitchenConfirm.title")}</h2>

			<PaymentDetail defaultExpanded />

			<div className="SendToKitchenConfirmFooterSpacer" />

			<div className="SendToKitchenConfirmFooter">
				<button
					className={`CheckoutButton ${isLoading ? "Disabled" : ""}`}
					onClick={handleConfirm}
					disabled={isLoading}
				>
					<div className="CheckoutButtonLabel">
						<Condition check={!isLoading}>{t("sendToKitchenConfirm.confirmBtn")}</Condition>
						<Condition check={isLoading}>
							<CircularProgress size={24} color="inherit" />
						</Condition>
					</div>
				</button>
			</div>

			<PopupDialog
				data-testid="send-to-kitchen-error-popup"
				open={showErrorPopup}
				title={t("sendToKitchenConfirm.errorTitle")}
				content={<span>{t("sendToKitchenConfirm.errorMessage")}</span>}
				buttons={errorButtons}
				onClose={() => setShowErrorPopup(false)}
				className="CheckoutErrorPopup"
			/>
		</div>
	);
};

export default SendToKitchenConfirmPage;
